import { HttpErrorResponse } from '@angular/common/http';
import {
  companyColumnMap,
  companyRelationsMap,
  isCondition,
  mapFilterColumns,
} from '@app/model';

/** The backend always pages by this size, whatever the client asks for. */
export const COMPANY_LIST_PAGE_SIZE = 100;

/**
 * fetchListOfCompany counts at most this many companies; a total of exactly
 * this value means "at least".
 */
export const COMPANY_COUNT_CAP = 200_000;

export type SortDescriptor = { selector: string; desc: boolean };

/**
 * The load options the company list request is built from: paging, sorting
 * and the filter expression. The lazy tables and the list source hand these
 * over, so they produce the same request body.
 */
export interface CompanyListLoadOptions {
  skip?: number;
  take?: number;
  sort?: SortDescriptor[] | null;
  filter?: unknown[] | null;
  /** The count of the query is known, the answer leaves it out. */
  skipCount?: boolean;
}

type OrderBy = {
  [field: string]: { sort: 'asc' | 'desc'; nulls: 'last' } | OrderBy;
};

const getSort = ({ selector, desc }: SortDescriptor): OrderBy => ({
  [companyRelationsMap[selector as keyof typeof companyRelationsMap] ||
  selector]: {
    sort: desc ? 'desc' : 'asc',
    nulls: 'last',
  },
});

export const buildFetchListOfCompanyRequest = (
  options: CompanyListLoadOptions | undefined,
  listId: number | null
) => {
  const orderBy =
    options && options.sort instanceof Array
      ? options.sort.reduce(
          (acc, cur) => ({
            ...acc,
            ...(companyRelationsMap[
              cur.selector as keyof typeof companyRelationsMap
            ]
              ? {
                  companies_latest: getSort(cur),
                }
              : getSort(cur)),
          }),
          {} as OrderBy
        )
      : null;

  return {
    method: 'fetchListOfCompany',
    data: {
      page:
        (options &&
          options.skip &&
          options.take &&
          options.skip / options.take) ||
        0,
      resultsPerPage: COMPANY_LIST_PAGE_SIZE,
      requestType: 'normal', // 'detailed',
      ...(orderBy ? { orderBy } : {}),
      ...(typeof listId === 'number' ? { listId } : {}),
      ...(options?.skipCount ? { skipCount: true } : {}),
      ...(options && options.filter instanceof Array
        ? {
            filter: isCondition(options.filter)
              ? [mapFilterColumns(options.filter, companyColumnMap)]
              : mapFilterColumns(options.filter, companyColumnMap),
          }
        : {}),
    },
  };
};

/**
 * Translation key of the message for a search that ran into the time limit of
 * the API (the Lambda answers 504 once its time limit, which differs per
 * stage, has passed). The message names no duration. The grid error banner
 * translates it; other messages pass through unchanged.
 */
export const SEARCH_TIMEOUT_MESSAGE = 'common.error.searchTimeout';

export const toCompanyListError = (e: HttpErrorResponse) =>
  new Error(
    e.status === 504 ? SEARCH_TIMEOUT_MESSAGE : e.statusText || e.message
  );
